import React from 'react';
import { useSelector } from 'react-redux';
import Divider from 'housecall-ui/dist/DesignSystem/DataDisplay/Divider/Divider';
import Tooltip from 'housecall-ui/dist/DesignSystem/DataDisplay/Tooltips/Tooltip';
import Typography from 'housecall-ui/dist/DesignSystem/DataDisplay/Typography/Typography';
import Box from 'housecall-ui/dist/DesignSystem/Layout/Box/Box';
import Grid from 'housecall-ui/dist/DesignSystem/Layout/Grid/Grid';
import makeStyles from 'housecall-ui/dist/DesignSystem/Styles/makeStyles';
import InfoOutlined from 'housecall-ui/dist/IconsAndImages/Icons/InfoOutlined';
import { HCPTheme } from 'housecall-ui/dist/Theme/Theme';
import { formatCurrency } from 'utils/currency';

import { PayWallFullState } from 'components/PayWall/Redux/Reducer.type';
import { getActivationFeeToDisplay, getSkipSetupFee } from 'components/PayWall/Redux/Selectors';

import { useBillingInfoStyles } from '../PayWallPlanSummary';

const useStyles = makeStyles((theme: HCPTheme) => ({
  strikeThroughText: {
    textDecoration: 'line-through',
    color: theme.palette.error.main,
  },
}));

function TaxSection() {
  const salesTaxDueToday = useSelector(
    (state: PayWallFullState) => state.payWall.planSummaryPreview.sales_tax_due_today
  );
  const salesTaxRate = useSelector((state: PayWallFullState) => state.payWall.planSummaryPreview.sales_tax_rate);

  const taxPercentageString = `(${salesTaxRate}%)`;
  const salesTaxAmount = formatCurrency(salesTaxDueToday);

  return (
    <>
      <Grid item xs={6}>
        <Box display="flex">
          <Typography type="body1" align="left" data-testid="tax-percentage">
            Tax {taxPercentageString}
          </Typography>
        </Box>
      </Grid>
      <Grid item xs={6}>
        <Typography variant="body1" align="right" data-testid="tax-amount">
          {salesTaxAmount}
        </Typography>
      </Grid>
    </>
  );
}

type ActivationFeeSectionProps = {
  activationFee: string;
  skipSetupFee: boolean | null;
};

function ActivationFeeSection({ activationFee, skipSetupFee }: ActivationFeeSectionProps) {
  const classes = useStyles();

  return (
    <>
      <Grid item xs={6}>
        Activation fee
      </Grid>
      <Grid item xs={6} data-testid="activation-fee-amount">
        <Typography variant="body1" align="right" className={skipSetupFee ? classes.strikeThroughText : undefined}>
          {formatCurrency(activationFee)}
        </Typography>
      </Grid>
    </>
  );
}

function ProratedSection({ proratedBalance }: { proratedBalance: string }) {
  const classes = useBillingInfoStyles();

  return (
    <>
      <Grid item xs={8}>
        <Box display="flex">
          <Typography variant="body1">Prorated credits</Typography>
          <Tooltip
            classes={{
              popper: classes.billInfoTooltip,
              tooltip: classes.billInfoTooltipText,
            }}
            title="This is a credit you get for services you have prepaid from now until your renewal date"
          >
            <InfoOutlined className={classes.billInfo} size="small" />
          </Tooltip>
        </Box>
      </Grid>
      <Grid item xs={4}>
        <Typography variant="body1" align="right" data-testid="prorated-balance-amount">
          ({formatCurrency(proratedBalance)})
        </Typography>
      </Grid>
    </>
  );
}

export default function SubtotalSection() {
  const activationFee = useSelector((state: PayWallFullState) => getActivationFeeToDisplay(state));
  const proratedBalance = useSelector((state: PayWallFullState) => state.payWall.planSummaryPreview.prorated_balance);
  const recurringAmount = useSelector((state: PayWallFullState) => state.payWall.planSummaryPreview.recurring_amount);
  const proratedSubtotal = useSelector((state: PayWallFullState) => state.payWall.planSummaryPreview.prorated_subtotal);
  const skipSetupFee = useSelector((state: PayWallFullState) => getSkipSetupFee(state));

  const hideActivationFeeSection = !activationFee || Number(activationFee) <= 0;
  const hideProratedBalanceSection = !proratedBalance || Number(proratedBalance) <= 0;

  return (
    <>
      <Grid container spacing={8} data-testid="subtotal">
        <Grid item xs={6}>
          <Typography variant="body1" align="left">
            Subtotal
          </Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography aria-label="subtotal price" variant="body1" align="right" data-testid="amount">
            {formatCurrency(recurringAmount)}
          </Typography>
        </Grid>

        {recurringAmount !== proratedSubtotal && (
          <>
            <Grid item xs={6}>
              <Typography variant="body1" align="left">
                Prorated subtotal
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography aria-label="subtotal price" variant="body1" align="right" data-testid="amount">
                {formatCurrency(proratedSubtotal)}
              </Typography>
            </Grid>
          </>
        )}

        <TaxSection />
        {!hideActivationFeeSection && (
          <ActivationFeeSection activationFee={activationFee} skipSetupFee={!!skipSetupFee} />
        )}
        {!hideProratedBalanceSection && <ProratedSection proratedBalance={proratedBalance} />}
      </Grid>

      <Box my={2}>
        <Divider />
      </Box>
    </>
  );
}
